import type {IPanelConfig, IStatsPanel, RenderingContext} from './types';

export abstract class AbstractPanel implements IStatsPanel {
    private readonly _name: string;
    private readonly _bg: string;
    private readonly _fg: string;
    private readonly _pr: number;
    private readonly _decimals: number;
    private readonly _maxValue: number;
    private readonly _canvas: HTMLCanvasElement;
    private readonly _context: CanvasRenderingContext2D;

    private _max: number;
    private _min: number;
    private _width!: number;
    private _height!: number;
    private _textX!: number;
    private _textY!: number;
    private _graphX!: number;
    private _graphY!: number;
    private _graphWidth!: number;
    private _graphHeight!: number;

    public constructor(name: string, bg: string, fg: string, maxValue: number, decimals = 0) {
        this._name = name;
        this._bg = bg;
        this._fg = fg;
        this._pr = 2;
        this._max = 0;
        this._min = Infinity;
        this._maxValue = maxValue;
        this._decimals = decimals;

        this._canvas = document.createElement('canvas');
        this._context = this._canvas.getContext('2d')!;
    }

    public get dom(): HTMLCanvasElement {
        return this._canvas;
    }

    public get maxValue(): number {
        return this._maxValue;
    }

    public init(config: IPanelConfig): void {
        const { _pr: pr, _bg: bg, _fg: fg, _name: name, _context: context } = this;

        const width = (this._width = config.width * pr);
        const height = (this._height = config.height * pr);
        const textX = (this._textX = 3 * pr);
        const textY = (this._textY = 3 * pr);
        const graphX = (this._graphX = 3 * pr);
        const graphY = (this._graphY = 14 * pr);
        const graphWidth = (this._graphWidth = width - graphX * pr);
        const graphHeight = (this._graphHeight = height - graphY - 3 * pr);

        this._canvas.width = width;
        this._canvas.height = height;

        context.font = `bold ${8 * pr}px Helvetica,Arial,sans-serif`;
        context.textBaseline = 'top';

        context.fillStyle = bg;
        context.fillRect(0, 0, width, height);

        context.fillStyle = fg;
        context.fillText(name, textX, textY);
        context.fillRect(graphX, graphY, graphWidth, graphHeight);

        context.fillStyle = bg;
        context.globalAlpha = 0.9;
        context.fillRect(graphX, graphY, graphWidth, graphHeight);
    }

    public render(): void {
        const {
            value,
            maxValue,

            _context: context,
            _canvas: canvas,
            _name: name,
            _bg: bg,
            _fg: fg,
            _pr: pr,
            _width: width,
            _textX: textX,
            _textY: textY,
            _graphX: graphX,
            _graphY: graphY,
            _graphWidth: graphWidth,
            _graphHeight: graphHeight
        } = this;

        const precision = Math.pow(10, this._decimals);
        const val = Math.round(value * precision) / precision;

        this._min = Math.min(this._min, val);
        this._max = Math.max(this._max, val);

        context.fillStyle = bg;
        context.globalAlpha = 1;
        context.fillRect(0, 0, width, graphY);
        context.fillStyle = fg;

        context.font = `bold ${8 * pr}px Helvetica,Arial,sans-serif`;
        context.textAlign = 'left';
        context.fillText(`${val} ${name}`, textX, textY);

        context.font = `bold ${7 * pr}px Helvetica,Arial,sans-serif`;
        context.textAlign = 'right';
        context.fillText(`${+this._min}-${this._max}`, width - 4 * pr, textY + 1 * pr);

        context.drawImage(
            canvas,
            graphX + pr,
            graphY,
            graphWidth - pr,
            graphHeight,
            graphX,
            graphY,
            graphWidth - pr,
            graphHeight
        );

        context.fillRect(graphX + graphWidth - pr, graphY, pr, graphHeight);

        context.fillStyle = bg;
        context.globalAlpha = 0.9;
        context.fillRect(graphX + graphWidth - pr, graphY, pr, Math.round((1 - value / maxValue) * graphHeight));
    }

    public abstract update(): void;
    public abstract onInstall(gl?: RenderingContext): void;
    public abstract onUninstall(): void;
    public abstract get value(): number;
}
